import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useQueryParam } from "@/hooks/useQueryParam";
import { clearContext } from "@/provider/DataContextProvider";
import { useLogout as useApiLogout } from "@/services/auth";
import {
  accessTokenAtom,
  addDeviceFormDataAtom,
  AlertTypes,
  refreshTokenAtom,
  showsSessionExpiredModalAtom,
  verifierAtom,
} from "@/utils/atoms";

import { useAlert } from "./useAlert";

export const useLogout = () => {
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setRefreshToken = useSetAtom(refreshTokenAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const setAddDeviceFormData = useSetAtom(addDeviceFormDataAtom);
  const showAlert = useAlert();

  const setVerifier = useSetAtom(verifierAtom);
  const [, setCode] = useQueryParam("code");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearAppContext = () => {
    const isPartnerRoute =
      window.location.hash.startsWith("#/partner") ||
      window.location.hash.startsWith("#/welcome-partner");
    clearContext();
    setCode("");
    queryClient.clear();
    setAccessToken(null);
    setRefreshToken(null);
    setVerifier(undefined);
    setShowSessionExpiredModal(undefined);
    setAddDeviceFormData(null);
    localStorage.clear();
    // Remove info from sessionStorage to show attention bubble again after login
    sessionStorage.removeItem("showFriendsWithBenefitsHint");

    // TODO: remove after debugging
    Sentry.addBreadcrumb({
      category: "logout",
      message: "clearing local storage after logout",
      level: "info",
      data: {
        source: "useLogout",
        flow: "clearLocalStorage",
      },
    });

    navigate(isPartnerRoute ? "/welcome-partner" : "/welcome");
  };

  const { mutate } = useApiLogout({
    mutation: {
      onSuccess: () => {
        clearAppContext();
      },
      onError: () => {
        clearAppContext();
      },
    },
  });

  // As this is commonly wired straight into button onClicks, React might call it with the SyntheticEvent...
  const logout = (emailVerificationErrorOrEvent?: string | SyntheticEvent) => {
    // ...so we need to avoid an event triggering an error alert
    const emailVerificationError =
      typeof emailVerificationErrorOrEvent === "string"
        ? emailVerificationErrorOrEvent
        : undefined;

    if (emailVerificationError) {
      // TODO: remove after debugging
      Sentry.addBreadcrumb({
        category: "logout",
        message: "error alert from emailVerificationError",
        level: "info",
        data: {
          source: "useLogout",
          flow: "emailVerificationError",
        },
      });

      showAlert({
        type: AlertTypes.error,
        text: emailVerificationError,
      });
    }

    mutate();
  };

  return logout;
};
